/**
 * Signaling message schemas for the WebRTC conferencing app.
 *
 * Wire format: JSON over WebSocket. Three message families discriminated
 * by a top-level `kind` field, then by `type` for the specific operation.
 *
 *   - request       Client → server, expects a matching response.
 *                   `{ kind: 'request',  id, type, data }`
 *   - response      Server → client, correlated by `id`.
 *                   `{ kind: 'response', id, ok: true,  data }`
 *                   `{ kind: 'response', id, ok: false, error: { code, message } }`
 *   - notification  Either direction, no response expected.
 *                   `{ kind: 'notification', type, data }`
 *
 * All schemas are zod; the corresponding TS types are inferred via
 * `z.infer<typeof Schema>` in src/index.ts.
 */
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Primitive identifiers and shared shapes
// ---------------------------------------------------------------------------

/** Room ID: short, URL-safe (typically generated by `nanoid(12)` on the server). */
export const RoomId = z.string().min(8).max(64)

/** Peer ID: assigned by the server on `join`; opaque to the client. */
export const PeerId = z.string().min(1).max(64)

/** Producer ID: server-assigned, returned from `produce`. */
export const ProducerId = z.string().min(1).max(128)

/** Consumer ID: server-assigned, returned from `consume`. */
export const ConsumerId = z.string().min(1).max(128)

/** Transport ID: server-assigned, returned from `createWebRtcTransport`. */
export const TransportId = z.string().min(1).max(128)

/** Display name a peer chose for themselves. */
export const DisplayName = z.string().trim().min(1).max(80)

/** Media kind exposed by mediasoup. */
export const MediaKind = z.enum(['audio', 'video'])

/** Transport direction relative to the client. */
export const TransportDirection = z.enum(['send', 'recv'])

/**
 * Catch-all for mediasoup blobs (DTLS params, ICE candidates, RTP params,
 * RTP capabilities, ...). We deliberately do NOT re-derive zod schemas
 * for the entire mediasoup wire format here — the library validates its
 * own payloads at the transport.connect / produce / consume calls.
 * Treating them as opaque `unknown` keeps the protocol independent of
 * mediasoup minor-version changes.
 */
export const Opaque = z.unknown()

/** Structured error returned in `ok: false` responses. */
export const ErrorCode = z.enum([
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'INVALID_STATE',
  'INTERNAL_ERROR',
  'NOT_ALLOWED',
])

export const ProtocolError = z.object({
  code: ErrorCode,
  message: z.string(),
})

// ---------------------------------------------------------------------------
// Request schemas (client → server) and their responses
// ---------------------------------------------------------------------------

/** join: enter a room and receive router capabilities + current peer list. */
export const JoinRequestData = z.object({
  roomId: RoomId,
  displayName: DisplayName,
  /**
   * Indicates how the display name was sourced.
   * - `'typed'`  (default, absent ⇒ treated as `'typed'`): visitor entered the
   *               name explicitly; the server admits it as-is (collisions allowed).
   * - `'guest'`:  name is a client-side candidate; the server MUST uniquify it
   *               against the live room participant set before admitting.
   */
  nameSource: z.enum(['typed', 'guest']).optional(),
  /**
   * Coarse device class of the joining client (010-mobile-receiver-quality).
   * `true` ⇒ a mobile/tablet device (see `isMobileDevice()` on the client).
   *
   * Backward-compatible and OPTIONAL: existing clients omit it and are treated
   * as non-mobile. The server uses it to cap the VP9 spatial layer it forwards
   * to this peer's video consumers, so a phone receives a lighter, decodable
   * stream instead of the publisher's full top spatial layer. Absent/false ⇒
   * the receiver keeps full per-receiver VP9 SVC adaptation (feature 006).
   */
  isMobile: z.boolean().optional(),
})

export const PeerSnapshotProducer = z.object({
  id: ProducerId,
  kind: MediaKind,
})

export const PeerSnapshot = z.object({
  peerId: PeerId,
  displayName: DisplayName,
  producers: z.array(PeerSnapshotProducer),
  mediaState: z.object({ mic: z.boolean(), cam: z.boolean() }),
})

export const JoinResponseData = z.object({
  peerId: PeerId,
  /**
   * The authoritative display name the server admitted this peer under.
   * For `nameSource: 'guest'` joins this may differ from the candidate the
   * client sent (the server uniquified it). Clients MUST update their local
   * label from this field rather than trusting the request value.
   */
  displayName: DisplayName,
  routerRtpCapabilities: Opaque,
  peers: z.array(PeerSnapshot),
})

/** createWebRtcTransport: ask the server to allocate a transport. */
export const CreateTransportRequestData = z.object({
  direction: TransportDirection,
})

export const CreateTransportResponseData = z.object({
  transportId: TransportId,
  iceParameters: Opaque,
  iceCandidates: Opaque,
  dtlsParameters: Opaque,
  sctpParameters: Opaque.optional(),
})

/** connectWebRtcTransport: deliver DTLS params after local SDP negotiation. */
export const ConnectTransportRequestData = z.object({
  transportId: TransportId,
  dtlsParameters: Opaque,
})

export const ConnectTransportResponseData = z.object({})

/** produce: notify the server we want to send a track. */
export const ProduceRequestData = z.object({
  transportId: TransportId,
  kind: MediaKind,
  rtpParameters: Opaque,
  appData: z.record(z.string(), z.unknown()).optional(),
})

export const ProduceResponseData = z.object({
  producerId: ProducerId,
})

/** consume: ask the server to set up a consumer for another peer's producer. */
export const ConsumeRequestData = z.object({
  producerId: ProducerId,
  rtpCapabilities: Opaque,
})

export const ConsumeResponseData = z.object({
  consumerId: ConsumerId,
  producerId: ProducerId,
  kind: MediaKind,
  rtpParameters: Opaque,
})

/** resumeConsumer: consumers are created paused, resume to start media flow. */
export const ResumeConsumerRequestData = z.object({
  consumerId: ConsumerId,
})

export const ResumeConsumerResponseData = z.object({})

/** closeProducer: tear down a producer when peer disables a track. */
export const CloseProducerRequestData = z.object({
  producerId: ProducerId,
})

export const CloseProducerResponseData = z.object({})

// Request envelope (discriminated by `type`, with `id` for correlation)
export const RequestType = z.enum([
  'join',
  'createWebRtcTransport',
  'connectWebRtcTransport',
  'produce',
  'consume',
  'resumeConsumer',
  'closeProducer',
])

export const Request = z.discriminatedUnion('type', [
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('join'),
    data: JoinRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('createWebRtcTransport'),
    data: CreateTransportRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('connectWebRtcTransport'),
    data: ConnectTransportRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('produce'),
    data: ProduceRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('consume'),
    data: ConsumeRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('resumeConsumer'),
    data: ResumeConsumerRequestData,
  }),
  z.object({
    kind: z.literal('request'),
    id: z.string(),
    type: z.literal('closeProducer'),
    data: CloseProducerRequestData,
  }),
])

// Response envelope (mirrors `id` from request; `ok` distinguishes success/error)
export const ResponseOk = z.object({
  kind: z.literal('response'),
  id: z.string(),
  ok: z.literal(true),
  data: z.unknown(),
})

export const ResponseErr = z.object({
  kind: z.literal('response'),
  id: z.string(),
  ok: z.literal(false),
  error: ProtocolError,
})

export const Response = z.discriminatedUnion('ok', [ResponseOk, ResponseErr])

// ---------------------------------------------------------------------------
// Notification schemas (either direction, no response)
// ---------------------------------------------------------------------------

// Server → client
export const NewPeerNotificationData = z.object({
  peerId: PeerId,
  displayName: DisplayName,
})

export const PeerLeftNotificationData = z.object({
  peerId: PeerId,
})

export const NewProducerNotificationData = z.object({
  peerId: PeerId,
  producerId: ProducerId,
  kind: MediaKind,
})

export const ProducerClosedNotificationData = z.object({
  peerId: PeerId,
  producerId: ProducerId,
})

export const PeerMediaStateChangedNotificationData = z.object({
  peerId: PeerId,
  mic: z.boolean().optional(),
  cam: z.boolean().optional(),
})

export const ServerShutdownNotificationData = z.object({
  reason: z.string().optional(),
})

// Client → server
export const SetMicEnabledNotificationData = z.object({ enabled: z.boolean() })
export const SetCamEnabledNotificationData = z.object({ enabled: z.boolean() })

export const NotificationType = z.enum([
  // server → client
  'newPeer',
  'peerLeft',
  'newProducer',
  'producerClosed',
  'peerMediaStateChanged',
  'serverShutdown',
  // client → server
  'setMicEnabled',
  'setCamEnabled',
])

export const Notification = z.discriminatedUnion('type', [
  z.object({
    kind: z.literal('notification'),
    type: z.literal('newPeer'),
    data: NewPeerNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('peerLeft'),
    data: PeerLeftNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('newProducer'),
    data: NewProducerNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('producerClosed'),
    data: ProducerClosedNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('peerMediaStateChanged'),
    data: PeerMediaStateChangedNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('serverShutdown'),
    data: ServerShutdownNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('setMicEnabled'),
    data: SetMicEnabledNotificationData,
  }),
  z.object({
    kind: z.literal('notification'),
    type: z.literal('setCamEnabled'),
    data: SetCamEnabledNotificationData,
  }),
])

// ---------------------------------------------------------------------------
// Top-level envelope: any incoming message is one of three shapes.
// Uses z.union (not discriminatedUnion) because the three branches are
// themselves discriminated unions and zod v3 doesn't support nesting.
// Each sub-schema is precise enough that ambiguity is impossible.
// ---------------------------------------------------------------------------

export const Message = z.union([Request, Response, Notification])
